import React, { useState, useEffect, useCallback, useContext, useRef, useMemo } from 'react';
import {
  Typography, Table, Tag, Button, Space, Select, message, Segmented, DatePicker, Spin, Tooltip,
} from 'antd';
import {
  PlusOutlined, SearchOutlined, TeamOutlined, UserOutlined, EyeOutlined,
  UnorderedListOutlined, TableOutlined, CalendarOutlined,
} from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import dayjs from 'dayjs';
import api from '../api';
import { UserContext } from '../App';
import CoachingCreateModal from '../components/CoachingCreateModal';

const STATUS_COLOR  = { draft: 'default', active: 'blue', completed: 'green', canceled: 'red' };
const STATUS_LABEL  = { draft: 'Draft', active: 'Active', completed: 'Completed', canceled: 'Canceled' };
const STATUS_BG     = { draft: '#fafafa', active: '#e6f4ff', completed: '#f6ffed', canceled: '#fff2f0' };
const STATUS_BORDER = { draft: '#d9d9d9', active: '#1677ff', completed: '#52c41a', canceled: '#ff4d4f' };

const WEEK_DAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

function canManage(user) {
  return (user?.CanEditCoaching ?? 0) >= 2;
}

let _cache = null;

// Status toggle (as on the Evaluations and Errors searches), searching at
// once: Open (Draft + Active, the old default) / Completed / All, unless
// statuses are picked in the dropdown, which then win; a toggle click clears
// the dropdown.
const STATUS_PRESETS = { open: ['draft', 'active'], completed: ['completed'], all: [] };

export default function CoachingSessionsPage() {
  const currentUser = useContext(UserContext);
  const navigate    = useNavigate();
  const restoredRef = useRef(!!_cache);

  const [sessions,       setSessions]       = useState(() => _cache?.sessions       ?? []);
  const [loading,        setLoading]        = useState(false);
  const [view,           setView]           = useState(() => _cache?.view           ?? 'mine');
  const [viewMode,       setViewMode]       = useState(() => _cache?.viewMode       ?? 'table');
  const [calMonth,       setCalMonth]       = useState(() => _cache?.calMonth       ?? dayjs().startOf('month'));
  const [statusMode,     setStatusMode]     = useState(() => _cache?.statusMode     ?? 'open');
  const [statusFilter,   setStatusFilter]   = useState(() => _cache?.statusFilter   ?? []);
  // Several request types at once; an older cached single value starts empty.
  const [reqTypeFilter,  setReqTypeFilter]  = useState(() => (Array.isArray(_cache?.reqTypeFilter) ? _cache.reqTypeFilter : []));
  const [areaFilter,     setAreaFilter]     = useState(() => (Array.isArray(_cache?.areaFilter) ? _cache.areaFilter : []));
  const [campaignFilter, setCampaignFilter] = useState(() => _cache?.campaignFilter ?? '');
  // Coach filter: only offered in the All view (the others are already "me").
  const [coachFilter,    setCoachFilter]    = useState(() => _cache?.coachFilter    ?? '');
  const [coaches,        setCoaches]        = useState([]);
  const [participantFilter, setParticipantFilter] = useState(() => _cache?.participantFilter ?? '');
  const [people,         setPeople]         = useState([]);
  const [dueDateFrom,    setDueDateFrom]    = useState(() => _cache?.dueDateFrom    ?? null);
  const [dueDateTo,      setDueDateTo]      = useState(() => _cache?.dueDateTo      ?? null);
  const [reqTypes,       setReqTypes]       = useState([]);
  const [areas,          setAreas]          = useState([]);
  const [campaigns,      setCampaigns]      = useState([]);
  const [createOpen,     setCreateOpen]     = useState(false);

  // over: { statusMode, statusFilter } to search with now (the status toggle)
  // before the state holding them has updated.
  const load = useCallback(async (overrideView, overrideViewMode, overrideCalMonth, over = {}) => {
    setLoading(true);
    const activeView     = overrideView     ?? view;
    const activeMode     = overrideViewMode ?? viewMode;
    const activeCalMonth = overrideCalMonth ?? calMonth;
    const sMode   = over.statusMode ?? statusMode;
    const sPicked = over.statusFilter ?? statusFilter;
    const statuses = sPicked?.length ? sPicked : STATUS_PRESETS[sMode];
    try {
      const params = { view: activeView };
      if (statuses?.length)     params.status          = statuses.join(',');
      if (reqTypeFilter.length) params.request_type_id = reqTypeFilter.join(',');
      if (areaFilter.length)    params.area_id         = areaFilter.join(',');
      if (campaignFilter)       params.campaign_id     = campaignFilter;
      if (coachFilter && activeView === 'mine') params.coach_user_id = coachFilter;
      if (participantFilter) params.participant_id = participantFilter;
      if (activeMode === 'calendar') {
        params.due_date_from = activeCalMonth.startOf('month').format('YYYY-MM-DD');
        params.due_date_to   = activeCalMonth.endOf('month').format('YYYY-MM-DD');
      } else {
        if (dueDateFrom) params.due_date_from = dueDateFrom.format('YYYY-MM-DD');
        if (dueDateTo)   params.due_date_to   = dueDateTo.format('YYYY-MM-DD');
      }
      const res = await api.get('/api/coaching/sessions', { params });
      setSessions(res.data);
      _cache = {
        sessions: res.data, view: activeView, viewMode: activeMode, calMonth: activeCalMonth,
        statusMode: sMode, statusFilter: sPicked,
        reqTypeFilter, areaFilter, campaignFilter, coachFilter, participantFilter, dueDateFrom, dueDateTo,
      };
    } catch { message.error('Failed to load coaching sessions.'); }
    finally { setLoading(false); }
  }, [view, viewMode, calMonth, statusMode, statusFilter, reqTypeFilter, areaFilter, campaignFilter, coachFilter, participantFilter, dueDateFrom, dueDateTo]);

  useEffect(() => {
    api.get('/api/coaching/request-types', { params: { all: '1' } })
      .then((r) => setReqTypes(r.data)).catch(() => {});
    api.get('/api/coaching/areas')
      .then((r) => setAreas(r.data || [])).catch(() => {});
    api.get('/api/coaching/campaigns')
      .then((r) => setCampaigns(r.data)).catch(() => {});
    api.get('/api/coaching/coaches')
      .then((r) => setCoaches(r.data || [])).catch(() => {});
    api.get('/api/coaching/active-users')
      .then((r) => setPeople(r.data || [])).catch(() => {});
  }, []);

  useEffect(() => { restoredRef.current = false; }, []); // eslint-disable-line
  useEffect(() => { if (_cache) load(); }, []); // eslint-disable-line

  const calDays = useMemo(() => {
    const start = calMonth.startOf('month').startOf('week');
    const end   = calMonth.endOf('month').endOf('week');
    const days  = [];
    let day = start;
    while (!day.isAfter(end)) {
      days.push(day);
      day = day.add(1, 'day');
    }
    return days;
  }, [calMonth]);

  const sessionsByDate = useMemo(() => {
    const map = {};
    sessions.forEach((s) => {
      if (s.DueDate) {
        const key = dayjs(s.DueDate).format('YYYY-MM-DD');
        if (!map[key]) map[key] = [];
        map[key].push(s);
      }
    });
    return map;
  }, [sessions]);

  const handleToggleMode = (newMode) => {
    setViewMode(newMode);
    load(undefined, newMode, calMonth);
  };

  const strSort  = (key) => (a, b) => (a[key] || '').localeCompare(b[key] || '');
  const dateSort = (key) => (a, b) => (a[key] || '').localeCompare(b[key] || '');

  const columns = [
    {
      // Fixed width, long titles cut short (full title on hover), to leave
      // room for the other columns.
      title: 'Title', dataIndex: 'Title', key: 'Title', width: 260, sorter: strSort('Title'),
      render: (v, r) => (
        <Button type="link" title={v}
          style={{ padding: 0, maxWidth: '100%', display: 'block', textAlign: 'left',
                   overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
          onClick={() => navigate(`/coaching/sessions/${r.ID}`)}>
          {v}
        </Button>
      ),
    },
    {
      title: 'Status', dataIndex: 'Status', key: 'Status', width: 100, sorter: strSort('Status'),
      render: (v) => <Tag color={STATUS_COLOR[v]}>{STATUS_LABEL[v] ?? v}</Tag>,
    },
    { title: 'Campaign',     dataIndex: 'Campaign',        key: 'Campaign',        width: 120, sorter: strSort('Campaign') },
    { title: 'Request Type', dataIndex: 'RequestTypeName', key: 'RequestTypeName', width: 140, sorter: strSort('RequestTypeName') },
    { title: 'Area',         dataIndex: 'AreaName',        key: 'AreaName',        width: 130, sorter: strSort('AreaName'),
      render: (v) => v || '-' },
    { title: 'Coach',        dataIndex: 'CoachName',       key: 'CoachName',       width: 160, sorter: strSort('CoachName') },
    { title: 'Due Date',     dataIndex: 'DueDate',         key: 'DueDate',         width: 110, sorter: dateSort('DueDate') },
    { title: 'Created On',   dataIndex: 'CreatedAt',       key: 'CreatedAt',       width: 115, sorter: dateSort('CreatedAt'),
      // Date only -- the time of day is not something anyone scans this list for.
      render: (v) => (v ? dayjs(v).format('YYYY-MM-DD') : '-') },
    {
      // Names rather than a count: who the session is with is the useful thing
      // to scan for. One name is shown inline; the rest sit behind a "+n" the
      // full list hovers off, so a big session cannot stretch the row.
      title: 'Participants', key: 'participants', width: 220,
      sorter: (a, b) => (a.ParticipantCount ?? 0) - (b.ParticipantCount ?? 0),
      render: (_, r) => {
        const names = (r.ParticipantNames || '').split(',').map((n) => n.trim()).filter(Boolean);
        if (!names.length) return <span style={{ color: '#bfbfbf' }}>-</span>;
        const done = `${r.CompletedCount ?? 0} of ${r.ParticipantCount ?? names.length} done`;
        return (
          <Tooltip title={<div>{names.map((n) => <div key={n}>{n}</div>)}<div style={{ marginTop: 4, opacity: 0.75 }}>{done}</div></div>}>
            <span>
              {names[0]}
              {names.length > 1 && <Tag color="blue" style={{ marginLeft: 6 }}>+{names.length - 1}</Tag>}
            </span>
          </Tooltip>
        );
      },
    },
    {
      // The counts kept their own column when Participants became names. The
      // names answer "who", these answer "how far along" -- putting the second
      // only in the names tooltip meant scanning for sessions with outstanding
      // participants needed a hover on every row.
      title: 'Qty / Done', key: 'participantCounts', width: 150, align: 'center',
      sorter: (a, b) => (a.CompletedCount ?? 0) - (b.CompletedCount ?? 0),
      render: (_, r) => (
        <Space size={4}>
          <Tag color="blue">{r.ParticipantCount ?? 0} total</Tag>
          <Tag color={r.CompletedCount === r.ParticipantCount && r.ParticipantCount > 0 ? 'green' : 'default'}>
            {r.CompletedCount ?? 0} done
          </Tag>
        </Space>
      ),
    },
  ];

  const today = dayjs().format('YYYY-MM-DD');

  return (
    <>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16, flexWrap: 'wrap', gap: 8 }}>
        <Typography.Title level={4} style={{ margin: 0 }}>Coaching Sessions</Typography.Title>
        {canManage(currentUser) && (
          <Button type="primary" icon={<PlusOutlined />} onClick={() => setCreateOpen(true)}>
            New Session
          </Button>
        )}
      </div>

      <Space wrap style={{ marginBottom: 16 }}>
        <Segmented
          value={viewMode}
          onChange={handleToggleMode}
          options={[
            { label: 'Table',    value: 'table',    icon: <TableOutlined /> },
            { label: 'Calendar', value: 'calendar', icon: <CalendarOutlined /> },
          ]}
        />
        <Segmented
          value={view}
          onChange={(v) => { setView(v); load(v); }}
          options={[
            { label: 'All',    value: 'mine',   icon: <UnorderedListOutlined /> },
            { label: "I'm a Coach",  value: 'coach',  icon: <UserOutlined /> },
            { label: "I'm a Viewer", value: 'viewer', icon: <EyeOutlined /> },
          ]}
        />
        <Segmented
          value={statusFilter?.length ? null : statusMode}
          onChange={(k) => { setStatusMode(k); setStatusFilter([]); load(undefined, undefined, undefined, { statusMode: k, statusFilter: [] }); }}
          options={[{ value: 'open', label: 'Open' }, { value: 'completed', label: 'Completed' }, { value: 'all', label: 'All' }]}
        />
        {view === 'mine' && (
          <Select
            allowClear showSearch optionFilterProp="label" placeholder="Any coach"
            value={coachFilter || undefined}
            onChange={(v) => setCoachFilter(v || '')}
            options={coaches.map((c) => ({ label: c.DisplayName, value: String(c.ID) }))}
            style={{ width: 200 }}
          />
        )}
        <Select
          allowClear showSearch optionFilterProp="label" placeholder="Any participant"
          value={participantFilter || undefined}
          onChange={(v) => setParticipantFilter(v || '')}
          options={people.map((u) => ({ label: u.TeamName ? `${u.DisplayName} (${u.TeamName})` : u.DisplayName, value: String(u.ID) }))}
          style={{ width: 220 }}
        />
        <Select
          allowClear placeholder="Any campaign"
          value={campaignFilter || undefined}
          onChange={(v) => setCampaignFilter(v || '')}
          options={campaigns.map((c) => ({ label: c.Name, value: String(c.ID) }))}
          style={{ width: 180 }}
        />
        <Select
          mode="multiple" allowClear placeholder="Specific statuses"
          value={statusFilter}
          onChange={setStatusFilter}
          options={Object.entries(STATUS_LABEL).map(([k, v]) => ({ label: v, value: k }))}
          maxTagCount="responsive"
          style={{ minWidth: 160 }}
        />
        <Select
          mode="multiple" allowClear showSearch optionFilterProp="label"
          placeholder="Any request type"
          value={reqTypeFilter}
          onChange={setReqTypeFilter}
          options={reqTypes.map((t) => ({ label: t.Name, value: String(t.ID) }))}
          maxTagCount="responsive"
          style={{ minWidth: 200 }}
        />
        <Select
          mode="multiple" allowClear showSearch optionFilterProp="label"
          placeholder="Any area"
          value={areaFilter}
          onChange={setAreaFilter}
          options={areas.map((a) => ({ label: a.Name, value: String(a.ID) }))}
          maxTagCount="responsive"
          style={{ minWidth: 180 }}
        />
        {viewMode === 'calendar' ? (
          <DatePicker
            picker="month"
            value={calMonth}
            onChange={(m) => { if (m) { setCalMonth(m); load(undefined, 'calendar', m); } }}
            format="MMMM YYYY"
            allowClear={false}
            style={{ width: 160 }}
          />
        ) : (
          <DatePicker.RangePicker
            value={[dueDateFrom, dueDateTo]}
            onChange={(dates) => {
              setDueDateFrom(dates?.[0] ?? null);
              setDueDateTo(dates?.[1] ?? null);
            }}
            format="YYYY-MM-DD"
            allowClear
            placeholder={['Due from', 'Due to']}
            style={{ width: 230 }}
          />
        )}
        {/* () => load(), not onClick={load}: load's first argument is the view,
            and passing the click event there stored the event as the view --
            the All / Coach / Viewer toggle then showed nothing selected. */}
        <Button type="primary" icon={<SearchOutlined />} onClick={() => load()} loading={loading}>Search</Button>
      </Space>

      {viewMode === 'table' ? (
        <Table
          rowKey="ID"
          dataSource={sessions}
          columns={columns}
          loading={loading}
          size="small"
          pagination={{ defaultPageSize: 25, showTotal: (t) => `${t} sessions` }}
          locale={{ emptyText: 'No coaching sessions found.' }}
        />
      ) : (
        <Spin spinning={loading}>
          <div style={{ border: '1px solid #f0f0f0', borderRadius: 8, overflow: 'hidden' }}>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', background: '#fafafa', borderBottom: '1px solid #f0f0f0' }}>
              {WEEK_DAYS.map((d) => (
                <div key={d} style={{ padding: '8px 0', textAlign: 'center', fontWeight: 600, fontSize: 12, color: '#595959' }}>
                  {d}
                </div>
              ))}
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)' }}>
              {calDays.map((day, i) => {
                const key            = day.format('YYYY-MM-DD');
                const isCurrentMonth = day.month() === calMonth.month();
                const isToday        = key === today;
                const daySessions    = sessionsByDate[key] || [];

                return (
                  <div
                    key={key}
                    style={{
                      minHeight: 110,
                      padding: '4px 6px',
                      borderRight: (i + 1) % 7 !== 0 ? '1px solid #f0f0f0' : 'none',
                      borderBottom: '1px solid #f0f0f0',
                      background: isCurrentMonth ? 'white' : '#fafafa',
                    }}
                  >
                    <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 3 }}>
                      <span style={{
                        fontSize: 12,
                        fontWeight: isToday ? 700 : 400,
                        color: isToday ? 'white' : (isCurrentMonth ? '#262626' : '#bfbfbf'),
                        background: isToday ? '#1677ff' : 'transparent',
                        borderRadius: '50%',
                        width: 20,
                        height: 20,
                        display: 'inline-flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}>
                        {day.date()}
                      </span>
                    </div>

                    {daySessions.map((s) => (
                      <div
                        key={s.ID}
                        onClick={() => navigate(`/coaching/sessions/${s.ID}`)}
                        style={{
                          marginBottom: 3,
                          padding: '3px 5px',
                          borderRadius: 4,
                          borderLeft: `3px solid ${STATUS_BORDER[s.Status] ?? '#d9d9d9'}`,
                          background: STATUS_BG[s.Status] ?? '#f5f5f5',
                          cursor: 'pointer',
                          fontSize: 11,
                          lineHeight: 1.4,
                          userSelect: 'none',
                        }}
                      >
                        {s.CoachName && (
                          <div style={{ color: '#595959', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', fontSize: 10 }}>
                            Coach: {s.CoachName}
                          </div>
                        )}
                        <div style={{ fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', color: '#262626' }}>
                          {s.Title}
                        </div>
                        <div style={{ color: '#595959', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {s.ParticipantCount === 1
                            ? (s.ParticipantNames || '1 participant')
                            : `${s.ParticipantCount} participant${s.ParticipantCount !== 1 ? 's' : ''}`}
                        </div>
                        {s.RequestTypeName && (
                          <div style={{ color: '#8c8c8c', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                            {s.RequestTypeName}
                          </div>
                        )}
                        <div style={{ color: STATUS_BORDER[s.Status] ?? '#8c8c8c', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', fontSize: 10, fontWeight: 500 }}>
                          {STATUS_LABEL[s.Status] ?? s.Status}
                        </div>
                      </div>
                    ))}
                  </div>
                );
              })}
            </div>
          </div>
        </Spin>
      )}

      <CoachingCreateModal
        open={createOpen}
        currentUser={currentUser}
        navigate={navigate}
        onClose={() => setCreateOpen(false)}
      />
    </>
  );
}
